<template>
  <div class="hike-tag-picker">
    <div class="row">
      <div class="col-12">
        <div class="accordion" :id="`${idPrefix}-activities`">
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button class="accordion-button collapsed px-0" type="button" data-bs-toggle="collapse" :data-bs-target="`#${idPrefix}-activities-collapse`" aria-expanded="true" :aria-controls="`${idPrefix}-activities-collapse`">
                <small><strong>{{ $t('plan_trail.save_modal.add_activity_tags') }}</strong></small>
              </button>
            </h2>
            <div :id="`${idPrefix}-activities-collapse`" class="accordion-collapse collapse" :data-bs-parent="`#${idPrefix}-activities`">
              <div class="accordion-body px-0">
                <div class="tag-list" data-testid="activity-tags">
                  <button
                    v-for="tag in availableActivityTags"
                    :key="`activity-${tag}`"
                    type="button"
                    class="tag-pill"
                    :class="{ active: activityTags.includes(tag) }"
                    :aria-pressed="activityTags.includes(tag)"
                    @click="$emit('update:activityTags', toggleCatalogTag(activityTags, tag))"
                  >
                    {{ formatTag(tag) }}
                  </button>
                  <p v-if="!availableActivityTags.length" class="text-muted small mb-0">{{ $t('plan_trail.save_modal.no_activity_tags_available') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <hr class="my-3">

    <div class="row">
      <div class="col-12">
        <div class="accordion" :id="`${idPrefix}-features`">
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button class="accordion-button collapsed px-0" type="button" data-bs-toggle="collapse" :data-bs-target="`#${idPrefix}-features-collapse`" aria-expanded="true" :aria-controls="`${idPrefix}-features-collapse`">
                <small><strong>{{ $t('plan_trail.save_modal.add_feature_tags') }}</strong></small>
              </button>
            </h2>
            <div :id="`${idPrefix}-features-collapse`" class="accordion-collapse collapse" :data-bs-parent="`#${idPrefix}-features`">
              <div class="accordion-body px-0">
                <div class="tag-list" data-testid="feature-tags">
                  <button
                    v-for="tag in availableFeatureTags"
                    :key="`feature-${tag}`"
                    type="button"
                    class="tag-pill"
                    :class="{ active: featureTags.includes(tag) }"
                    :aria-pressed="featureTags.includes(tag)"
                    @click="$emit('update:featureTags', toggleCatalogTag(featureTags, tag))"
                  >
                    {{ formatTag(tag) }}
                  </button>
                  <p v-if="!availableFeatureTags.length" class="text-muted small mb-0">{{ $t('plan_trail.save_modal.no_feature_tags_available') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <hr class="my-3">

    <div class="row">
      <div class="col-12">
        <div class="accordion" :id="`${idPrefix}-custom`">
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button class="accordion-button collapsed px-0" type="button" data-bs-toggle="collapse" :data-bs-target="`#${idPrefix}-custom-collapse`" aria-expanded="true" :aria-controls="`${idPrefix}-custom-collapse`">
                <small><strong>{{ $t('plan_trail.save_modal.add_tags') }}</strong></small>
              </button>
            </h2>
            <div :id="`${idPrefix}-custom-collapse`" class="accordion-collapse collapse" :data-bs-parent="`#${idPrefix}-custom`">
              <div class="accordion-body px-0">
                <div class="input-group mb-3">
                  <input
                    v-model="newCustomTag"
                    type="text"
                    class="form-control"
                    :placeholder="$t('plan_trail.save_modal.create_a_tag')"
                    maxlength="50"
                    :aria-label="$t('plan_trail.save_modal.create_a_tag_2')"
                    @keydown.enter.prevent="submitCustomTag"
                  >
                  <button
                    type="button"
                    class="btn btn-secondary"
                    data-testid="add-custom-tag"
                    :disabled="!newCustomTag.trim()"
                    @click="submitCustomTag"
                  >
                    {{ $t('plan_trail.save_modal.add') }}
                  </button>
                </div>
                <div class="tag-list" data-testid="custom-tags">
                  <button
                    v-for="tag in customOptions"
                    :key="`custom-${tag}`"
                    type="button"
                    class="tag-pill"
                    :class="{ active: isCustomTagSelected(customTags, tag) }"
                    :aria-pressed="isCustomTagSelected(customTags, tag)"
                    @click="$emit('update:customTags', toggleCustomTag(customTags, tag))"
                  >
                    {{ tag }}
                  </button>
                  <p v-if="!customOptions.length" class="text-muted small mb-0">{{ $t('plan_trail.save_modal.type_above_to_create_your_first') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import {
  addCustomTag,
  customTagOptions,
  formatTag,
  isCustomTagSelected,
  toggleCatalogTag,
  toggleCustomTag,
} from '../hikeTagSelection';

/**
 * The Activities / Features / Tags accordions a hike or recording is tagged with.
 * Presentational only — the catalog and the selection come in as props and every
 * change goes out as an `update:*` event (so `v-model:featureTags` etc. work), which
 * lets the planner keep its Vuex store and the recording editor its local state.
 */
export default defineComponent({
  name: 'HikeTagPicker',
  props: {
    // Bootstrap's collapse targets elements by id, so two pickers on one page need
    // different prefixes.
    idPrefix: { type: String, default: 'hike-tags' },
    availableFeatureTags: { type: Array as PropType<string[]>, default: () => [] },
    availableActivityTags: { type: Array as PropType<string[]>, default: () => [] },
    availableCustomTags: { type: Array as PropType<string[]>, default: () => [] },
    featureTags: { type: Array as PropType<string[]>, default: () => [] },
    activityTags: { type: Array as PropType<string[]>, default: () => [] },
    customTags: { type: Array as PropType<string[]>, default: () => [] },
  },
  emits: ['update:featureTags', 'update:activityTags', 'update:customTags'],
  data() {
    return { newCustomTag: '' };
  },
  computed: {
    customOptions(): string[] {
      return customTagOptions(this.availableCustomTags, this.customTags);
    },
  },
  methods: {
    formatTag,
    isCustomTagSelected,
    toggleCatalogTag,
    toggleCustomTag,
    submitCustomTag() {
      const updated = addCustomTag(this.customTags, this.newCustomTag);
      if (updated !== this.customTags) {
        this.$emit('update:customTags', updated);
      }
      this.newCustomTag = '';
    },
  },
});
</script>

<style scoped>
.accordion-item {
  border: none;
}

.accordion-header, .accordion-button {
  background: transparent;
}

.accordion-button::after {
  background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23ffffff'><path fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/></svg>");
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag-pill {
  background: var(--gray, #22242A);
  border: 1px solid #495057;
  border-radius: 999px;
  color: #f8f9fa;
  padding: 0.35rem 0.9rem;
  font-size: 0.875rem;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.tag-pill:hover,
.tag-pill:focus {
  background: var(--gray-raised, #2A2C34);
  border-color: #6c757d;
  color: #fff;
}

.tag-pill.active {
  background: #0d6efd;
  border-color: #0d6efd;
  color: #fff;
}

.tag-pill:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}
</style>
